import {
    fighterEnties,
    fighterEntryDisplayMapping,
    genders,
    paymentMethod,
    weightClassesName,
} from "../constants";
import {
    getAgeAtDate,
    getEventFighterWeightClass,
    isMinorAtDate,
} from "./common";

/**
 * Pure filtering + sorting for the admin "Event Fighters" panel.
 *
 * The whole roster is already fetched client-side on the manage-event page
 * (every `event-fighters` doc joined with its `fighters` doc), so everything
 * here is in-memory: no Firestore queries, no indexes, no pagination.
 *
 * Two units of work:
 *   - a fighter  — an `eventFighter` with `.fighter` joined on it
 *   - a bout     — a pair `{ fighterA, fighterB }` of those; a bout survives
 *                  the filter when *either* corner does.
 *
 * Filter algebra: OR within a group, AND across groups. Flags are no
 * exception — picking "No receipt" and "Refunded" means "either".
 *
 * No React in this file. It is unit-tested by event-fighter-filters.test.js.
 */

/* ------------------------------------------------------------------ *
 * Age bands
 * ------------------------------------------------------------------ */

export const AGE_BANDS = [
    { value: "u18", label: "U18", max: 17 },
    { value: "18-24", label: "18–24", min: 18, max: 24 },
    { value: "25-34", label: "25–34", min: 25, max: 34 },
    { value: "35+", label: "35+", min: 35 },
];

/**
 * Bucket a whole-year age into one of AGE_BANDS.
 * @param {number|null|undefined} age
 * @returns {string|null} band value, or null when the age is unknown
 */
export function ageBandOf(age) {
    const n = Number(age);
    if (age === null || age === undefined || !Number.isFinite(n)) return null;
    if (n <= 17) return "u18";
    if (n <= 24) return "18-24";
    if (n <= 34) return "25-34";
    return "35+";
}

/**
 * A fighter's age as of the event date, falling back to the stored
 * `fighter.age` when the birthdate is missing or unparseable.
 * @param {object} eventFighter
 * @param {*} [eventDate]
 * @returns {number|null}
 */
export function ageOfEventFighter(eventFighter, eventDate) {
    const fighter = eventFighter?.fighter;
    if (!fighter) return null;
    const atEvent = getAgeAtDate(fighter.birthdate, eventDate);
    if (atEvent !== null && atEvent !== undefined && Number.isFinite(atEvent)) {
        return atEvent;
    }
    const stored = Number(fighter.age);
    return Number.isFinite(stored) ? stored : null;
}

/* ------------------------------------------------------------------ *
 * Flags
 * ------------------------------------------------------------------ */

export const FLAGS = [
    {
        value: "minor",
        label: "Minor ≤17",
        test: (ef, opts) =>
            isMinorAtDate(ef?.fighter?.birthdate, opts?.eventDate),
    },
    {
        value: "noreceipt",
        label: "No receipt",
        test: (ef) => !ef?.receipt,
    },
    {
        value: "credit",
        label: "Paid by credit",
        test: (ef) => ef?.paymentMethod === paymentMethod.token,
    },
    {
        value: "refunded",
        label: "Refunded",
        test: (ef) =>
            !!ef?.isRefunded ||
            (Array.isArray(ef?.refundHistory) && ef.refundHistory.length > 0),
    },
    {
        // A pending WEIGHT_CHANGE request is a flag on a fighter, not a roster
        // of its own — so it composes with Entry / Weight / Bout here instead
        // of hiding behind a fifth tab.
        value: "weightchange",
        label: "Weight change",
        test: (ef, opts) =>
            !!opts?.pendingWeightChangeIds?.has?.(ef?.fighterId),
    },
    {
        value: "rematch",
        label: "Needs rematch review",
        test: (ef) => ef?.needsRematchReview === true,
    },
];

const flagByValue = (value) => FLAGS.find((f) => f.value === value) ?? null;

/* ------------------------------------------------------------------ *
 * Facet groups
 *
 * `key` doubles as the URL param name. `values` is the full universe for
 * the group; `poolFiltered` groups (Entry, Weight) only show the values
 * actually present in the tab's pool.
 * ------------------------------------------------------------------ */

export const FACET_GROUPS = [
    {
        key: "entry",
        label: "Entry",
        poolFiltered: true,
        values: fighterEnties.map((entry) => ({
            value: entry,
            label: fighterEntryDisplayMapping[entry] ?? entry,
        })),
        matches: (ef, value) => ef?.entry === value,
    },
    {
        key: "wc",
        label: "Weight",
        poolFiltered: true,
        values: Object.keys(weightClassesName).map((key) => ({
            value: key,
            label: `${key} · ${weightClassesName[key]}`,
        })),
        matches: (ef, value) => getEventFighterWeightClass(ef) === value,
    },
    {
        key: "gender",
        label: "Gender",
        values: Object.keys(genders).map((key) => ({
            value: key,
            label: genders[key],
        })),
        matches: (ef, value) =>
            String(ef?.fighter?.gender ?? "").toUpperCase() ===
            String(value).toUpperCase(),
    },
    {
        key: "age",
        label: "Age",
        values: AGE_BANDS.map((b) => ({ value: b.value, label: b.label })),
        matches: (ef, value, opts) =>
            ageBandOf(ageOfEventFighter(ef, opts?.eventDate)) === value,
    },
    {
        key: "bout",
        label: "Bout",
        values: [
            { value: "matched", label: "Matched" },
            { value: "unmatched", label: "Unmatched" },
        ],
        matches: (ef, value) =>
            (ef?.matchWith ? "matched" : "unmatched") === value,
    },
    {
        key: "flag",
        label: "Flags",
        values: FLAGS.map((f) => ({ value: f.value, label: f.label })),
        matches: (ef, value, opts) => {
            const flag = flagByValue(value);
            return flag ? !!flag.test(ef, opts) : false;
        },
    },
];

export const FACET_GROUP_KEYS = FACET_GROUPS.map((g) => g.key);

export const getFacetGroup = (key) =>
    FACET_GROUPS.find((g) => g.key === key) ?? null;

export const facetValueLabel = (groupKey, value) =>
    getFacetGroup(groupKey)?.values.find((v) => v.value === value)?.label ??
    value;

/* ------------------------------------------------------------------ *
 * Filter state
 * ------------------------------------------------------------------ */

/** @returns {{q: string, entry: string[], wc: string[], gender: string[], age: string[], bout: string[], flag: string[]}} */
export function emptyFilters() {
    const filters = { q: "" };
    FACET_GROUP_KEYS.forEach((key) => {
        filters[key] = [];
    });
    return filters;
}

/** Tolerate partial/undefined filter objects coming from callers. */
export function normalizeFilters(filters) {
    const next = emptyFilters();
    if (!filters) return next;
    next.q = typeof filters.q === "string" ? filters.q : "";
    FACET_GROUP_KEYS.forEach((key) => {
        const value = filters[key];
        next[key] = Array.isArray(value) ? value.filter(Boolean) : [];
    });
    return next;
}

export function hasActiveFilters(filters) {
    const f = normalizeFilters(filters);
    if (f.q.trim()) return true;
    return FACET_GROUP_KEYS.some((key) => f[key].length > 0);
}

/**
 * How many things are "on" — each non-empty facet group counts once, and a
 * non-empty search counts once. This is the number on the Filters button.
 */
export function activeGroupCount(filters) {
    const f = normalizeFilters(filters);
    return (
        FACET_GROUP_KEYS.filter((key) => f[key].length > 0).length +
        (f.q.trim() ? 1 : 0)
    );
}

/** Immutably add/remove one value from one group. */
export function toggleFilterValue(filters, groupKey, value) {
    const next = normalizeFilters(filters);
    if (!FACET_GROUP_KEYS.includes(groupKey)) return next;
    next[groupKey] = next[groupKey].includes(value)
        ? next[groupKey].filter((v) => v !== value)
        : [...next[groupKey], value];
    return next;
}

/** Short human labels for the crumb chips on the result meta line. */
export function filterCrumbs(filters) {
    const f = normalizeFilters(filters);
    const crumbs = [];
    if (f.q.trim()) crumbs.push(`“${f.q.trim()}”`);
    FACET_GROUPS.forEach((group) => {
        const selected = f[group.key];
        if (!selected.length) return;
        crumbs.push(
            selected.map((v) => facetValueLabel(group.key, v)).join(", ")
        );
    });
    return crumbs;
}

/* ------------------------------------------------------------------ *
 * Predicate
 * ------------------------------------------------------------------ */

const SEARCH_FIELDS = [
    "name",
    "gymNameOrTeamName",
    "nationality",
    "icOrPassport",
];

/** Lower-cased blob the search box substring-matches against. */
export function searchHaystack(eventFighter) {
    const fighter = eventFighter?.fighter ?? {};
    return SEARCH_FIELDS.map((key) =>
        fighter[key] === null || fighter[key] === undefined
            ? ""
            : String(fighter[key])
    )
        .join(" ")
        .toLowerCase();
}

/**
 * Does this event fighter survive the filters?
 *
 * @param {object} eventFighter
 * @param {object} filters
 * @param {{eventDate?: *, skipGroup?: string}} [opts] `skipGroup` ignores one
 *   group (or "q"), which is how facet counts exclude their own group.
 * @returns {boolean}
 */
export function matchesFilters(eventFighter, filters, opts = {}) {
    if (!eventFighter) return false;
    const f = normalizeFilters(filters);
    const skip = opts?.skipGroup;

    if (skip !== "q") {
        const q = f.q.trim().toLowerCase();
        if (q && !searchHaystack(eventFighter).includes(q)) return false;
    }

    for (const group of FACET_GROUPS) {
        if (group.key === skip) continue;
        const selected = f[group.key];
        if (!selected.length) continue;
        // OR within the group...
        const hit = selected.some((value) =>
            group.matches(eventFighter, value, opts)
        );
        // ...AND across groups.
        if (!hit) return false;
    }
    return true;
}

/** The two corners of a bout, accepting `{fighterA, fighterB}` or `{pair}`. */
export function pairCorners(item) {
    const pair = item?.pair ?? item;
    return [pair?.fighterA, pair?.fighterB].filter(Boolean);
}

/** A bout survives when either corner does. */
export function matchesFiltersPair(pair, filters, opts = {}) {
    return pairCorners(pair).some((ef) => matchesFilters(ef, filters, opts));
}

/* ------------------------------------------------------------------ *
 * Facet options + counts
 * ------------------------------------------------------------------ */

/**
 * Which chips to render for a group, given the tab's pool.
 * @param {string} groupKey
 * @param {Array} pool fighters, or bouts when `opts.pairs` is true
 * @param {{eventDate?: *, pairs?: boolean}} [opts]
 */
export function facetOptions(groupKey, pool, opts = {}) {
    const group = getFacetGroup(groupKey);
    if (!group) return [];
    if (!group.poolFiltered) return group.values;
    const list = Array.isArray(pool) ? pool : [];
    const fighters = opts?.pairs
        ? list.flatMap((item) => pairCorners(item))
        : list;
    return group.values.filter((option) =>
        fighters.some((ef) => ef && group.matches(ef, option.value, opts))
    );
}

/**
 * "How many would I see if I picked this chip" — the group being counted is
 * excluded from the predicate, so a chip never reads 0 and then finds rows.
 *
 * @param {Array} pool fighters, or bouts when `opts.pairs` is true
 * @param {object} filters
 * @param {string} groupKey
 * @param {string} value
 * @param {{eventDate?: *, pairs?: boolean}} [opts]
 * @returns {number}
 */
export function countFacet(pool, filters, groupKey, value, opts = {}) {
    const group = getFacetGroup(groupKey);
    if (!group || !Array.isArray(pool)) return 0;
    const without = { ...opts, skipGroup: groupKey };

    if (opts?.pairs) {
        return pool.filter((item) =>
            pairCorners(item).some(
                (ef) =>
                    matchesFilters(ef, filters, without) &&
                    group.matches(ef, value, opts)
            )
        ).length;
    }

    return pool.filter(
        (ef) =>
            matchesFilters(ef, filters, without) &&
            group.matches(ef, value, opts)
    ).length;
}

/* ------------------------------------------------------------------ *
 * Sorting
 * ------------------------------------------------------------------ */

export const ROSTER_SORTS = [
    { value: "registered", label: "Registered" },
    { value: "name", label: "Name" },
    { value: "weight", label: "Weight class" },
    { value: "entry", label: "Entry" },
    { value: "age", label: "Age" },
    { value: "height", label: "Height" },
    { value: "wins", label: "Wins" },
    { value: "gym", label: "Gym / Team" },
];

export const REFUND_SORTS = [
    ...ROSTER_SORTS,
    { value: "credits", label: "Credits held" },
];

export const PAIR_SORTS = [
    { value: "bout", label: "Bout order" },
    { value: "entry", label: "Entry" },
    { value: "weight", label: "Weight class" },
    { value: "name", label: "Red corner" },
];

export const DEFAULT_ROSTER_SORT = "registered";
export const DEFAULT_PAIR_SORT = "bout";

export function sortsForTab(tab) {
    if (tab === "matched") return PAIR_SORTS;
    if (tab === "refunded") return REFUND_SORTS;
    return ROSTER_SORTS;
}

export function defaultSortForTab(tab) {
    return tab === "matched" ? DEFAULT_PAIR_SORT : DEFAULT_ROSTER_SORT;
}

export function isValidSortForTab(tab, sortKey) {
    return sortsForTab(tab).some((s) => s.value === sortKey);
}

const str = (value) => (value === null || value === undefined ? "" : String(value));

const millisOf = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === "function") {
        try {
            return value.toMillis();
        } catch {
            return 0;
        }
    }
    if (typeof value.toDate === "function") {
        try {
            return value.toDate().getTime();
        } catch {
            return 0;
        }
    }
    if (typeof value.seconds === "number") return value.seconds * 1000;
    if (typeof value._seconds === "number") return value._seconds * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
};

const numOf = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const entryLabelOf = (ef) =>
    fighterEntryDisplayMapping[ef?.entry] ?? str(ef?.entry);

const winsOf = (ef) => numOf(ef?.fighter?.records?.[ef?.entry]?.win);

const creditsOf = (ef, opts) =>
    numOf(opts?.tokenCounts?.[ef?.fighterId] ?? 0);

const ROSTER_COMPARATORS = {
    registered: (a, b) => millisOf(a?.createdAt) - millisOf(b?.createdAt),
    name: (a, b) =>
        str(a?.fighter?.name).localeCompare(str(b?.fighter?.name)),
    weight: (a, b) =>
        str(getEventFighterWeightClass(a)).localeCompare(
            str(getEventFighterWeightClass(b))
        ),
    entry: (a, b) => entryLabelOf(a).localeCompare(entryLabelOf(b)),
    age: (a, b, opts) =>
        numOf(ageOfEventFighter(a, opts?.eventDate)) -
        numOf(ageOfEventFighter(b, opts?.eventDate)),
    height: (a, b) => numOf(a?.fighter?.height) - numOf(b?.fighter?.height),
    wins: (a, b) => winsOf(a) - winsOf(b),
    gym: (a, b) =>
        str(a?.fighter?.gymNameOrTeamName).localeCompare(
            str(b?.fighter?.gymNameOrTeamName)
        ),
    credits: (a, b, opts) => creditsOf(a, opts) - creditsOf(b, opts),
};

/**
 * Sort a list of event fighters. Array#sort is stable, so equal keys keep
 * their incoming order.
 *
 * @param {Array} list
 * @param {string} sortKey
 * @param {"asc"|"desc"} [direction]
 * @param {{eventDate?: *, tokenCounts?: object}} [opts]
 * @returns {Array} a new array
 */
export function sortEventFighters(list, sortKey, direction = "asc", opts = {}) {
    const items = Array.isArray(list) ? list.slice() : [];
    const compare =
        ROSTER_COMPARATORS[sortKey] ?? ROSTER_COMPARATORS[DEFAULT_ROSTER_SORT];
    const sign = direction === "desc" ? -1 : 1;
    return items.sort((a, b) => sign * compare(a, b, opts));
}

const redCorner = (item) => pairCorners(item)[0] ?? null;

const PAIR_COMPARATORS = {
    bout: (a, b) => numOf(a?.originalIndex) - numOf(b?.originalIndex),
    entry: (a, b, opts) =>
        ROSTER_COMPARATORS.entry(redCorner(a), redCorner(b), opts),
    weight: (a, b, opts) =>
        ROSTER_COMPARATORS.weight(redCorner(a), redCorner(b), opts),
    name: (a, b, opts) =>
        ROSTER_COMPARATORS.name(redCorner(a), redCorner(b), opts),
};

/**
 * Put bouts into the admin's saved running order.
 *
 * `boutOrder` is a list of pairKeys stored on the event. It is treated as a
 * preference, not a contract: keys for bouts that no longer exist are ignored,
 * and bouts missing from it (a pair matched after the last reorder) are
 * appended in their existing order rather than dropped. That way an unsaved or
 * stale order can never hide a fight from the card.
 *
 * @param {Array<{pairKey: string}>} entries
 * @param {Array<string>|null|undefined} boutOrder
 * @returns {Array} a new array, renumbered so `originalIndex` is the bout number
 */
export function applyBoutOrder(entries, boutOrder) {
    const items = Array.isArray(entries) ? entries.slice() : [];
    const order = Array.isArray(boutOrder) ? boutOrder : [];
    if (order.length === 0) {
        return items.map((entry, index) => ({ ...entry, originalIndex: index }));
    }

    const rank = new Map();
    order.forEach((key, index) => {
        if (!rank.has(key)) rank.set(key, index);
    });

    // Stable: anything unranked keeps its relative position, after the ranked.
    const ranked = [];
    const unranked = [];
    items.forEach((entry) => {
        (rank.has(entry?.pairKey) ? ranked : unranked).push(entry);
    });
    ranked.sort((a, b) => rank.get(a.pairKey) - rank.get(b.pairKey));

    return ranked
        .concat(unranked)
        .map((entry, index) => ({ ...entry, originalIndex: index }));
}

/**
 * Move one bout to a new position and return the full list of pairKeys.
 *
 * Works off the COMPLETE ordered list, not the filtered view, so dragging while
 * a filter is on still produces a sane running order for the whole card.
 *
 * @param {Array<string>} pairKeys every bout, in current order
 * @param {string} movingKey the bout being moved
 * @param {string|null} beforeKey drop it in front of this bout; null = the end
 * @returns {Array<string>} a new array
 */
export function moveBout(pairKeys, movingKey, beforeKey) {
    const keys = Array.isArray(pairKeys) ? pairKeys.slice() : [];
    const from = keys.indexOf(movingKey);
    if (from < 0 || movingKey === beforeKey) return keys;

    keys.splice(from, 1);
    const target = beforeKey == null ? -1 : keys.indexOf(beforeKey);
    if (target < 0) keys.push(movingKey);
    else keys.splice(target, 0, movingKey);
    return keys;
}

/**
 * Sort bout entries. Each entry carries its ORIGINAL bout index — the number
 * admins refer to ("bout 7") — so sorting never renumbers a card.
 *
 * @param {Array<{pair?: object, fighterA?: object, fighterB?: object, originalIndex: number}>} entries
 * @param {string} sortKey
 * @param {"asc"|"desc"} [direction]
 * @param {object} [opts]
 * @returns {Array} a new array
 */
export function sortPairEntries(entries, sortKey, direction = "asc", opts = {}) {
    const items = Array.isArray(entries) ? entries.slice() : [];
    const compare =
        PAIR_COMPARATORS[sortKey] ?? PAIR_COMPARATORS[DEFAULT_PAIR_SORT];
    const sign = direction === "desc" ? -1 : 1;
    return items.sort((a, b) => sign * compare(a, b, opts));
}

/* ------------------------------------------------------------------ *
 * URL round trip
 * ------------------------------------------------------------------ */

const readParam = (searchParams, key) => {
    if (!searchParams) return null;
    if (typeof searchParams.get === "function") return searchParams.get(key);
    return searchParams[key] ?? null;
};

/**
 * Read filters out of a URLSearchParams (or a plain object). Unknown values
 * are dropped so a hand-edited URL can't poison the view.
 */
export function parseFiltersFromParams(searchParams) {
    const filters = emptyFilters();
    filters.q = readParam(searchParams, "q") || "";

    FACET_GROUPS.forEach((group) => {
        const raw = readParam(searchParams, group.key);
        if (!raw) return;
        const allowed = new Set(group.values.map((v) => v.value));
        const picked = String(raw)
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s && allowed.has(s));
        filters[group.key] = [...new Set(picked)];
    });

    return filters;
}

/**
 * The inverse: a plain param object with everything at its default omitted,
 * ready to hand to react-router's setSearchParams.
 */
export function filtersToParams(filters) {
    const f = normalizeFilters(filters);
    const params = {};
    if (f.q.trim()) params.q = f.q.trim();
    FACET_GROUP_KEYS.forEach((key) => {
        if (f[key].length) params[key] = f[key].join(",");
    });
    return params;
}
